import { AllowedIcons } from '@thyme/nashville/src/types/icons'
import { computed, SetupContext } from 'vue'
import type { TToastMessage, TToastProps } from './types'
import type { ToastProps } from 'primevue/toast'

const CLOSE_ICON = AllowedIcons.close

/** Icon slots, keyed by the field name they share with their stack-wide prop. */
type IconField = 'headerIcon' | 'actionIcon'

/**
 * @param props
 * @param context
 */
export function setup(props: TToastProps, context: SetupContext) {
  // `createProp` widens enum props to `string`; narrow it back for PrimeVue.
  const toastPosition = computed(() => props.position as ToastProps['position'])

  /**
   * Per-message icon; uses stack default if icon not supplied, no icon rendered if explicit null passed in
   *
   * Reads `props[field]` under the same key as the message, so each slot's prop
   * name has to keep matching its message field name.
   * @param message - The message being rendered.
   * @param field - Which icon slot to resolve.
   */
  function iconFor(message: TToastMessage, field: IconField) {
    if (message[field] === null) {
      return undefined
    }
    return message[field] ?? props[field]
  }

  /**
   * @param message - The message being rendered.
   */
  function actionButtonProps(message: TToastMessage) {
    return {
      label: message.actionLabel,
      icon: iconFor(message, 'actionIcon'),
      iconPos: 'right',
      iconSize: 'xs',
      size: 'small',
      'data-testid': 'toast-action',
    }
  }

  /**
   * Hand the action click back to the caller, does not dismiss toast
   * @param message - The message whose action was clicked.
   */
  function onAction(message: TToastMessage) {
    context.emit('action', message)
  }

  /**
   * User-initiated dismissal only -- not fired for `life` expiry or
   * `toast.remove()` / `removeAllGroups()`.
   * @param message - The message being dismissed.
   * @param closeCallback - PrimeVue's handler; clears the life timer.
   */
  function onClose(message: TToastMessage, closeCallback: () => void) {
    closeCallback()
    context.emit('close', message)
  }

  return {
    actionButtonProps,
    closeIcon: CLOSE_ICON,
    iconFor,
    onAction,
    onClose,
    toastPosition,
  }
}
